// Lib
import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

// Components
import DrawingEditorCanvasDrawMode from './DrawingEditorCanvasDrawMode';
import DrawingEditorCanvasEraseMode from './DrawingEditorCanvasEraseMode';
import DrawingEditorCanvasSelectMode from './selectMode/DrawingEditorCanvasSelectMode';
import DrawingEditorCanvasReadOnlyMode from './DrawingEditorCanvasReadOnlyMode';
import DrawingEditorCanvasInputManager from './inputManager/DrawingEditorCanvasInputManager';
import { getDrawingInputLayerClassName } from '../drawingEditorInputLayerUtils';

// Constants
import { TOOL_MODES } from '../drawingEditorConstants';

/**
 * Chooses the canvas mode component for the active tool and editability state.
 */
const getCanvasContentComponent = (toolMode, isEditable) => {
    if (!isEditable) return DrawingEditorCanvasReadOnlyMode;

    switch (toolMode) {
        case TOOL_MODES.SELECT:
            return DrawingEditorCanvasSelectMode;
        case TOOL_MODES.ERASE:
            return DrawingEditorCanvasEraseMode;
        default:
            return DrawingEditorCanvasDrawMode;
    }
};

const DrawingEditorCanvas = forwardRef(function DrawingEditorCanvas(props, ref) {
    const { toolMode, isEditable, externalInputLayerRef } = props;

    const drawingCanvasRootRef = useRef();
    const internalInputLayerRef = useRef();
    const inputLayerDelegateRef = useRef();

    const ContentComponent = getCanvasContentComponent(toolMode, isEditable);
    const inputLayerRef = externalInputLayerRef || internalInputLayerRef;
    const shouldRenderInternalInputLayer = !!isEditable && !externalInputLayerRef;
    /**
     * Proxies pointer callbacks to whichever mode delegate is currently active.
     */
    const inputLayerDelegate = useMemo(
        () => ({
            onPointerDown: (args) => {
                inputLayerDelegateRef.current?.onPointerDown?.(args);
            },
            onPointerMove: (args) => {
                inputLayerDelegateRef.current?.onPointerMove?.(args);
            },
            onPointerUp: (args) => {
                inputLayerDelegateRef.current?.onPointerUp?.(args);
            },
            onPointerCancel: (args) => {
                inputLayerDelegateRef.current?.onPointerCancel?.(args);
            },
        }),
        [],
    );

    const setDrawingCanvasRef = useCallback(
        (node) => {
            drawingCanvasRootRef.current = node;

            if (!ref) return;
            ref.current = node;
        },
        [ref],
    );

    return (
        <div className="DrawingEditorCanvas" ref={setDrawingCanvasRef}>
            <ContentComponent
                {...props}
                externalInputLayerRef={inputLayerRef}
                drawingCanvasRootRef={drawingCanvasRootRef}
                inputLayerDelegateRef={inputLayerDelegateRef}
            />
            {isEditable && (
                <DrawingEditorCanvasInputManager
                    delegate={inputLayerDelegate}
                    drawingCanvasRootRef={drawingCanvasRootRef}
                    inputLayerRef={inputLayerRef}
                    canvasScale={props.canvasScale}
                    zoomScale={props.zoomScale}
                    viewBoxX={props.viewBoxX}
                    viewBoxY={props.viewBoxY}
                />
            )}
            {shouldRenderInternalInputLayer && (
                <div ref={internalInputLayerRef} className={getDrawingInputLayerClassName(toolMode)} />
            )}
        </div>
    );
});

DrawingEditorCanvas.propTypes = {
    isEditable: PropTypes.bool,
    toolMode: PropTypes.string,
    externalInputLayerRef: PropTypes.object,
    canvasScale: PropTypes.number,
    zoomScale: PropTypes.number,
    viewBoxX: PropTypes.number,
    viewBoxY: PropTypes.number,
};

export default DrawingEditorCanvas;
